'use client';

import Image from 'next/image';
import { motion } from 'framer-motion';
import { useMemo, useRef, useEffect, useState } from 'react';

interface ImageCarouselProps {
  images: { src: string; alt: string }[];
  className?: string;
}

export function ImageCarousel({ images, className = '' }: ImageCarouselProps) {
  const [cycleDistance, setCycleDistance] = useState(0);
  const measureRef = useRef<HTMLDivElement>(null);
  
  // Create just enough repetitions for seamless scrolling
  const manyImages = useMemo(() => {
    const result = [];
    for (let i = 0; i < 10; i++) { // Reduced from 20 to 10
      result.push(...images);
    }
    return result;
  }, [images]);
  
  // Measure the actual width of one complete image set after images load
  useEffect(() => {
    if (measureRef.current && cycleDistance === 0) {
      const measureImages = () => {
        const imageElements = measureRef.current?.querySelectorAll('img');
        if (imageElements && imageElements.length >= images.length) {
          let totalWidth = 0;
          // Sum up the width of the first set of images (one complete cycle)
          for (let i = 0; i < images.length; i++) {
            totalWidth += imageElements[i].offsetWidth;
          }
          if (totalWidth > 0) {
            setCycleDistance(totalWidth);
          }
        }
      };

      // Wait a bit for images to load and render
      const timer = setTimeout(measureImages, 500);
      
      // Also try measuring on window load
      const handleLoad = () => measureImages();
      window.addEventListener('load', handleLoad);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('load', handleLoad);
      };
    }
  }, [images.length, cycleDistance]);

  const startPosition = 0;

  return (
    <div className={`relative overflow-hidden bg-gradient-to-r from-gray-50 to-gray-100 max-h-[200px] ${className}`}>
      <div className="relative">
        <motion.div
          ref={measureRef}
          className="flex"
          initial={{ x: startPosition }}
          animate={cycleDistance > 0 ? { x: startPosition - cycleDistance } : { x: startPosition }}
          transition={{
            duration: cycleDistance > 0 ? cycleDistance / 40 : 0,
            ease: 'linear',
            repeat: cycleDistance > 0 ? Infinity : 0,
            repeatType: 'loop',
          }}
        >
          {manyImages.map((image, index) => (
            <div
              key={index}
              className="flex-shrink-0 relative h-[200px]" // Fixed height, auto width
            >
              <Image
                src={image.src}
                alt={image.alt}
                width={800}
                height={200}
                className="h-full w-auto object-cover" // Height fixed, width auto
                sizes="(min-width: 768px) 50vw, 100vw"
                priority={index < 10}
              />
            </div>
          ))}
        </motion.div>
        
        {/* Gradient overlays for smooth edges */}
        <div className="absolute left-0 top-0 h-full w-20 bg-gradient-to-r from-gray-50 to-transparent pointer-events-none" />
        <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-gray-100 to-transparent pointer-events-none" />
      </div>
    </div>
  );
} 